import { useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Badge, Box, Button, InputAdornment, LinearProgress, Pagination, TextField, Typography } from "@mui/material";
import { Close, FileUpload, Search } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";

import { COLOR_CONSTANTS } from "@/helpers/Colors";
import LoaderV2 from "@/pages/InvoiceManagement/components/LoaderV2";
import { useToast } from "@/pages/InvoiceManagement/hooks/useToast";
import InvoiceReview from "@/pages/InvoiceManagement/InvoiceReview";
import StatusFilter from "@/pages/InvoiceManagement/components/StatusFilter";
import { filterData } from "@/pages/InvoiceManagement/constants/filters";

import { useInvoiceList, useInvoiceSummary, useV1InvoiceDetail } from "../api/invoiceQueries";
import { useDeleteInvoice } from "../api/invoiceMutations";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { getCities } from "@/services/invoiceService";

import { invoiceKeys } from "../api/queryKeys";
import StatusFilterSidebar from "../components/StatusFilterSidebar";
import InvoiceTable from "../components/InvoiceTable";
import MobileInvoiceList, { type ExtraFilterDef } from "../components/MobileInvoiceList";
import UploadInvoicesModal from "../modals/UploadInvoicesModal";

import DeleteInvoiceModal from "../modals/DeleteInvoiceModal";
import { useInvoiceUploadFlow } from "../hooks/useInvoiceUploadFlow";
import { useBatchManager } from "../hooks/useBatchManager";
import { FILE_UPLOAD_STATUS } from "../types/attachment";
import { V2_COLORS } from "../constants/colors";
import { getDefaultFilterStatus, getFilterCount, getSidebarFilters } from "../constants/sidebarFilters";
import { LIFECYCLE_STATUS } from "../types/common";

import type { V2InvoiceListItem } from "../types/invoice";
import { usePermissions } from "@/context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "@/constants/bouncerPermissions";
import { UserContext } from "@/context/UserContext";
import { useIsMobile } from "@/hooks/useIsMobile";

const PAGE_SIZE = 20;

/**
 * Extra (non-status) filters shared by the desktop StatusFilter dropdown and the
 * mobile filter drawer. `validation_status` is repurposed as the payment filter.
 */
function buildInvoiceExtraFilters(): ExtraFilterDef[] {
  return filterData("invoice")
    .filter((f: Record<string, unknown>) => f.filter_key !== "invoice_status")
    .map((f: Record<string, unknown>) =>
      f.filter_key === "validation_status"
        ? {
            title: "Payment",
            filter_key: "payment_status",
            type: "checkbox",
            filters: [
              { label: "Initiated", value: "initiated" },
              { label: "Not done", value: "not_done" },
            ],
          }
        : f,
    ) as unknown as ExtraFilterDef[];
}

export default function InvoiceList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get("search") ?? "");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteInvoice, setDeleteInvoice] = useState<V2InvoiceListItem | null>(null);
  const [v1ReviewId, setV1ReviewId] = useState<number | null>(null);
  const { showToast } = useToast();
  const { hasPermission } = usePermissions();
  const { invoiceFactory } = useContext(UserContext);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteInvoice();
  const canUpload = hasPermission(BOUNCER_PERMISSIONS.INVOICE_UPLOAD);
  const isInternal = hasPermission(BOUNCER_PERMISSIONS.ACCOUNTS_PAYABLE_VIEW);
  const isMobile = useIsMobile();
  const extraFilterData = useMemo(buildInvoiceExtraFilters, []);

  // v1 review modal — fetch full invoice detail for old invoices
  const { data: v1InvoiceDetail, isLoading: isV1DetailLoading, isError: isV1DetailError } = useV1InvoiceDetail(v1ReviewId);

  useEffect(() => {
    if (isV1DetailError && v1ReviewId) {
      showToast({ type: "error", message: "Failed to load invoice details" });
      setV1ReviewId(null);
    }
  }, [isV1DetailError, v1ReviewId, showToast]);

  // Batch manager — owns all active/completed batch streams.
  // onBatchComplete auto-refreshes the invoice list when any batch finishes OCR.
  const batchManager = useBatchManager({
    onBatchComplete: useCallback(() => {
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
    }, [queryClient]),
  });

  // Upload flow — hands off to batchManager on batch start, then resets to idle.
  const flow = useInvoiceUploadFlow({
    onBatchStarted: batchManager.addBatch,
  });

  const defaultStatus = getDefaultFilterStatus(isInternal);
  // `?page=abc`, an empty `?page=`, 0 or a negative would send offset=NaN or
  // a negative offset; read them all as page 1 (same guard as PaymentList).
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const status = searchParams.get("status") ?? defaultStatus;
  const search = searchParams.get("search") || undefined;
  const [filter, setFilter] = useState<Record<string, unknown>>(() => {
    try {
      const raw = searchParams.get("filterData");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  // Resolve selected sidebar filter → lifecycle status array for the API
  const filters = getSidebarFilters(isInternal);
  const selectedFilter = filters.find((f) => f.status === status);
  const lifecycleStatuses = selectedFilter?.lifecycleStatuses ?? [];

  // Mirror the v1 factory_code filter: omit when "all" or not set
  const factoryCode =
    invoiceFactory?.item_code && invoiceFactory.item_code !== "all"
      ? invoiceFactory.item_code
      : undefined;

  const { isLoading: isCitiesLoading, data: citiesData } = useQuery(["citiesQuery"], getCities, {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // Non-status filters go to BOTH the list and the summary, so the sidebar counts and
  // the pagination total describe the same result set. The sidebar status group is
  // deliberately not sent to the summary — it needs counts for every group.
  const sharedFilters = { search, factory_code: factoryCode, ...filter };

  const { data, isLoading, isFetching, isError, error } = useInvoiceList({
    limit: PAGE_SIZE,
    offset: PAGE_SIZE * (page - 1),
    ...(lifecycleStatuses.length > 0 ? { lifecycle_status: lifecycleStatuses } : {}),
    ...sharedFilters,
  });

  // With no active filter the filtered summary is byte-identical to the global one, so
  // pass `undefined` to share the query key (one request, and it also serves the
  // vendor rejected-alert below).
  const hasActiveFilter = Object.values(sharedFilters).some((v) => v != null && v !== "");
  const { data: summaryData } = useInvoiceSummary(hasActiveFilter ? sharedFilters : undefined);

  // The vendor "N invoice(s) rejected — resubmit" alert must reflect the vendor's TRUE
  // rejected count, not the currently filtered view — a vendor searching an unrelated
  // PO still has invoices to fix. So it reads a separate, unfiltered summary. Internal
  // users never see the alert, so the extra request is skipped for them.
  const { data: globalSummaryData } = useInvoiceSummary(undefined, { enabled: !isInternal });

  const handleSearch = useCallback(() => {
    setSearchParams((prev) => {
      if (searchInput.trim()) {
        prev.set("search", searchInput.trim());
      } else {
        prev.delete("search");
      }
      prev.set("page", "1");
      return prev;
    });
  }, [searchInput, setSearchParams]);

  const handleSearchClear = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("search");
      prev.set("page", "1");
      return prev;
    });
  }, [setSearchParams]);

  const handleFilter = useCallback((filterState: Record<string, unknown>) => {
    const cleaned = Object.fromEntries(
      Object.entries(filterState).filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== "" && v != null
      )
    );
    setFilter(cleaned);
    setSearchParams((prev) => {
      if (Object.keys(cleaned).length > 0) {
        prev.set("filterData", JSON.stringify(cleaned));
      } else {
        prev.delete("filterData");
      }
      prev.set("page", "1");
      return prev;
    });
  }, [setSearchParams]);

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        prev.set("page", String(newPage));
        return prev;
      });
    },
    [setSearchParams]
  );

  const handleStatusChange = useCallback(
    (newStatus: string | null) => {
      setSearchParams((prev) => {
        if (newStatus) {
          prev.set("status", newStatus);
        } else {
          prev.delete("status");
        }
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const handleRowClick = useCallback(
    (invoice: V2InvoiceListItem) => {
      navigate(`/dashboard/invoice-management/v2/${invoice.id}`);
    },
    [navigate]
  );

  const handleReview = useCallback(
    (invoice: V2InvoiceListItem) => {
      if (!invoice.lifecycle_status) {
        // Old v1 invoice — open v1 InvoiceReview inline
        setV1ReviewId(invoice.id);
      } else {
        navigate(`/dashboard/invoice-management/v2/${invoice.id}?review=true`);
      }
    },
    [navigate]
  );

  const handleEdit = useCallback((invoice: V2InvoiceListItem) => {
    navigate(`/dashboard/invoice-management/v2/${invoice.id}/edit`);
  }, [navigate]);

  const handleCreditNote = useCallback((invoice: V2InvoiceListItem) => {
    const cnId = invoice.credit_notes?.[0]?.id;
    if (!cnId) return;
    navigate(`/dashboard/invoice-management/v2/${invoice.id}?cn=${cnId}`);
  }, [navigate]);

  const handleDelete = useCallback((invoice: V2InvoiceListItem) => {
    setDeleteInvoice(invoice);
  }, []);

  // --- Upload modal close handler ---
  const hasUnresolved = flow.items.some((i) => i.status !== FILE_UPLOAD_STATUS.UPLOADED);

  const handleUploadClose = useCallback(() => {
    if (flow.phase === "uploading") {
      const confirmed = window.confirm(
        "Upload is in progress. Closing will cancel all remaining uploads. Continue?",
      );
      if (!confirmed) return;
      flow.cancelAll();
      flow.reset();
    } else if (flow.phase === "complete" && !hasUnresolved) {
      // All upload failures were resolved — safe to reset
      flow.reset();
    }
    // "complete" with failures: just close, state persists for retry on reopen
    // "idle"/"selected": just close
    setUploadOpen(false);
  }, [flow, hasUnresolved]);

  const handleV1ReviewClose = useCallback(() => {
    setV1ReviewId(null);
    // v1 InvoiceReview invalidates ["invoiceListQuery"] (v1 key) — also invalidate v2 keys
    queryClient.invalidateQueries(invoiceKeys.lists());
    queryClient.invalidateQueries(invoiceKeys.summary());
  }, [queryClient]);

  // Show rejected invoices alert for vendors — from the UNFILTERED summary (see above)
  const rejectedCount =
    !isInternal ? (globalSummaryData?.status_counts?.[LIFECYCLE_STATUS.REJECTED] ?? 0) : 0;

  if (isLoading) return <LoaderV2 />;

  if (isError) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
        <Typography variant="h6" color="error">
          Error loading invoices
        </Typography>
        <Typography color="textSecondary">
          {(error as Error)?.message ?? "Please try again later"}
        </Typography>
      </Box>
    );
  }

  const invoices = data?.invoices ?? [];
  const statusCounts = summaryData?.status_counts ?? {};

  // Page total for the header + pagination. The list's total_count is being dropped by
  // the BE (ENG-26609); once it is gone, derive the same number from the summary for the
  // selected sidebar group. Until the summary has responded the total is unknown, and the
  // header count and pagination wait rather than rendering a misleading 0.
  const summaryTotal = summaryData
    ? getFilterCount(selectedFilter, statusCounts, summaryData.total_count)
    : undefined;
  const totalCount = data?.total_count ?? summaryTotal;

  // Modals + v1 review overlay are identical on both layouts — rendered once from here.
  const sharedModals = (
    <>
      <UploadInvoicesModal
        open={uploadOpen}
        onClose={handleUploadClose}
        phase={flow.phase}
        items={flow.items}
        summary={flow.summary}
        rejectedFiles={flow.rejectedFiles}
        batchStartError={flow.batchStartError}
        activeBatches={batchManager.batches}
        onDismissBatch={batchManager.dismissBatch}
        onFilesSelected={flow.addFiles}
        onUploadAll={flow.startUpload}
        onRemoveFile={flow.cancelFile}
        onRetryFile={flow.retryFile}
        onRetryAllFailed={flow.retryAllFailed}
        onRetryBatchProcessing={flow.retryBatchProcessing}
        onDismissRejected={flow.dismissRejected}
      />

      {deleteInvoice && (
        <DeleteInvoiceModal
          open={!!deleteInvoice}
          loading={deleteMutation.isLoading}
          invoiceNo={deleteInvoice.invoice_no}
          onClose={() => setDeleteInvoice(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleteInvoice.id, {
              onSuccess: () => {
                setDeleteInvoice(null);
                showToast({ type: "success", message: "Invoice deleted" });
              },
              onError: () => {
                showToast({ type: "error", message: "Failed to delete invoice" });
              },
            });
          }}
        />
      )}

      {/* V1 review modal — loading overlay */}
      {v1ReviewId && isV1DetailLoading && (
        <Box
          sx={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: "rgba(0,0,0,0.3)", display: "flex",
            alignItems: "center", justifyContent: "center", zIndex: 1300,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* V1 review modal — full-screen InvoiceReview for old invoices */}
      {v1ReviewId && v1InvoiceDetail && (
        <InvoiceReview
          invoice={v1InvoiceDetail}
          handleClose={handleV1ReviewClose}
        />
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileInvoiceList
          invoices={invoices}
          totalCount={totalCount ?? 0}
          pageSize={PAGE_SIZE}
          page={page}
          onPageChange={setPage}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSearch={handleSearch}
          onSearchClear={handleSearchClear}
          isInternal={isInternal}
          canUpload={canUpload}
          statusFilters={filters}
          selectedStatus={status}
          statusCounts={statusCounts}
          summaryTotalCount={summaryData?.total_count ?? 0}
          onStatusChange={handleStatusChange}
          onRowClick={handleRowClick}
          onReview={handleReview}
          onEdit={handleEdit}
          onCreditNote={handleCreditNote}
          activeBatchCount={batchManager.activeBatchCount}
          onUploadOpen={() => setUploadOpen(true)}
          isFetching={isFetching && !!data}
          rejectedCount={rejectedCount}
          extraFilterData={extraFilterData}
          extraFilters={filter}
          onExtraFilterChange={handleFilter}
          cities={citiesData || []}
          citiesLoading={isCitiesLoading}
        />

        {sharedModals}
      </>
    );
  }

  return (
    <Box display="flex" alignItems="start" justifyContent="flex-start" pt={2} px={2} flex={1} minHeight={0}>
      <StatusFilterSidebar
        isInternal={isInternal}
        statusCounts={statusCounts}
        totalCount={summaryData?.total_count ?? 0}
        selectedStatus={status}
        onStatusChange={handleStatusChange}
      />

      <Box ml={3} flex={1} minWidth={0} sx={{ display: "flex", flexDirection: "column", overflow: "hidden", alignSelf: "stretch" }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={1} alignItems="center">
            <Typography fontWeight={700} fontSize="16px" lineHeight="24px">
              Invoice List
            </Typography>
            {totalCount != null && (
              <Typography fontSize="12px" lineHeight="16px" color={V2_COLORS.textSecondary}>
                ({totalCount})
              </Typography>
            )}
            <StatusFilter
              filterData={extraFilterData}
              applyFilter={handleFilter}
              selectedFilters={filter}
              cities={citiesData || []}
              citiesLoading={isCitiesLoading}
            />
          </Box>
          <Box display="flex" gap={1.5} alignItems="center">
            <TextField
              size="small"
              placeholder="Search by Vendor, Order or Invoice ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              sx={{ width: 320, backgroundColor: "#fff" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: "#999" }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <Close
                      sx={{ fontSize: 16, color: "#999", cursor: "pointer" }}
                      onClick={() => { setSearchInput(""); handleSearchClear(); }}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />
            {canUpload && (
              <Badge
                badgeContent={batchManager.activeBatchCount}
                color="error"
                invisible={batchManager.activeBatchCount === 0}
                sx={{ "& .MuiBadge-badge": { fontSize: "10px", height: 16, minWidth: 16 } }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FileUpload fontSize="small" />}
                  onClick={() => setUploadOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    height: 36,
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                >
                  Upload Invoices
                </Button>
              </Badge>
            )}
          </Box>
        </Box>

        {/* Rejected alert for vendors */}
        {rejectedCount > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {rejectedCount} invoice(s) rejected — review remarks and resubmit.
          </Alert>
        )}

        {/* Refetch indicator */}
        {isFetching && data && (
          <LinearProgress sx={{ mb: 1, borderRadius: "4px" }} />
        )}

        <Box sx={{ flex: 1, minHeight: 0, width: "100%", overflowY: "auto", overflowX: "auto", bgcolor: "background.default", borderRadius: "8px" }}>
          <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
            {/* Invoice Table */}
            <Box sx={{ opacity: isFetching && data ? 0.5 : 1, transition: "opacity 0.15s" }}>
              <InvoiceTable
                invoices={invoices}
                onRowClick={handleRowClick}
                onReview={handleReview}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreditNote={handleCreditNote}
              />
            </Box>
          </Box>
        </Box>

        {/* Pagination */}
        {totalCount != null && totalCount > PAGE_SIZE && (
          <Box display="flex" justifyContent="flex-end" py={2}>
            <Pagination
              count={Math.ceil(totalCount / PAGE_SIZE)}
              variant="outlined"
              shape="rounded"
              page={page}
              onChange={(_, p) => setPage(p)}
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: COLOR_CONSTANTS["on-primary/variant"],
                  backgroundColor: COLOR_CONSTANTS["primary/variant"],
                  borderColor: COLOR_CONSTANTS["on-primary/variant"],
                },
              }}
            />
          </Box>
        )}
      </Box>

      {sharedModals}
    </Box>
  );
}
